import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PageHeader from './PageHeader';

import { API } from '../apiClient';
function api(path, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(token ? {Authorization: `Bearer ${token}`} : {}),
    ...(options.headers || {})
  };
  if (!(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  return fetch(`${API}${path}`, {
    ...options,
    headers
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function reservationNumber(job) {
  const raw = job?.reservation_number || job?.external_id || '';
  const text = String(raw);
  if (!text) return '';
  return text.includes(':') ? text.split(':').pop() : text;
}

function jobLabel(job, staff = false) {
  if (!job) return 'Bez przypisania';
  const number = reservationNumber(job);
  if (staff) return `${job.scheduled_date} · rezerwacja ${number || job.id}`;
  return `${job.scheduled_date} — ${job.guest_name || `rezerwacja ${number || job.id}`}`;
}

function money(value) {
  return `${Number(value || 0).toFixed(2)} zł`;
}

function reviewLabel(status) {
  if (status === 'ok') return 'OK — można zwrócić';
  if (status === 'ai_pending') return 'AI sprawdza paragon';
  if (status === 'needs_clarification') return 'Do doprecyzowania';
  return 'Czeka na sprawdzenie';
}

function reimbursementState(row) {
  if (row.reimbursement_confirmed_at || row.reimbursed) {
    return { label: 'Zwrócone', className: 'ack-badge' };
  }
  if ((row.review_status || 'pending') === 'needs_clarification') {
    return { label: 'Do doprecyzowania', className: 'expense-clarify' };
  }
  return { label: 'Do zwrotu', className: 'expense-pending' };
}

function isReimbursed(row) {
  return !!(row.reimbursement_confirmed_at || row.reimbursed);
}

function expenseBucket(row) {
  if (isReimbursed(row)) return 'archive';
  const status = row.review_status || 'pending';
  if (status === 'needs_clarification') return 'clarify';
  if (status === 'ok') return 'payable';
  return 'checking';
}

function filterExpenses(rows, filter) {
  if (filter === 'all') return rows;
  if (filter === 'active') return rows.filter(row => !isReimbursed(row));
  return rows.filter(row => expenseBucket(row) === filter);
}

function countByFilter(rows, filter) {
  return filterExpenses(rows, filter).length;
}

function formatClock(iso) {
  if (!iso) return '';
  // SQLite now() format: 'YYYY-MM-DD HH:MM:SS' (localtime in worker) or ISO.
  const m = String(iso).match(/(\d{2}:\d{2})/);
  return m ? m[1] : String(iso);
}

function formatDate(iso) {
  if (!iso) return '';
  const d = String(iso).split(' ')[0];
  return d;
}

function ReviewStatus({ row }) {
  const status = row.review_status || 'pending';
  let flags = [];
  try { flags = JSON.parse(row.review_flags || '[]'); } catch { flags = []; }
  const receiptFlag = flags.find(f => f.startsWith('receipt:'));
  const visionFlag = flags.find(f => f.startsWith('vision:'));
  const visionLabel = visionFlag === 'vision:error' ? 'błąd usługi wizyjnej'
    : visionFlag === 'vision:unreadable' || visionFlag === 'vision:low_quality' ? 'zdjęcie nieczytelne'
    : visionFlag ? visionFlag.replace('vision:', '') : '';
  return (
    <div className={`review-status review-${status}`}>
      <strong>{reviewLabel(status)}</strong>
      {row.review_message && <small>{row.review_message}</small>}
      {receiptFlag && <small>Powód: {receiptFlag.replace('receipt:', '')}</small>}
      {visionFlag && <small>Powód: {visionLabel}</small>}
      {status === 'ai_pending' && row.ai_review_due_at && (
        <small className="review-meta">Planowane sprawdzenie: {formatDate(row.ai_review_due_at)} {formatClock(row.ai_review_due_at)}</small>
      )}
      {row.reviewed_at && (
        <small className="review-meta">
          Sprawdzone: {formatDate(row.reviewed_at)} {formatClock(row.reviewed_at)}
          {row.reviewed_by && <> · {row.reviewed_by}</>}
        </small>
      )}
    </div>
  );
}

function receiptUrl(path) {
  return path ? `${API}/${path}` : '';
}

export default function Expenses({ expenses, jobs, reload, user }) {
  const ctx = useOutletContext() || {};
  const safeUser = user || ctx.user || {};
  const safeJobs = jobs || ctx.jobs || [];
  const allExpenses = expenses || ctx.expenses || [];
  const safeReload = reload || ctx.reload;
  const isStaff = safeUser.role === 'staff';
  const safeExpenses = isStaff ? allExpenses.filter(e => e.user_id === safeUser.id) : allExpenses;
  const visibleJobs = isStaff ? safeJobs.filter(j => j.is_current_action || j.status !== 'done') : safeJobs;
  const [form, setForm] = useState({
    user_id: safeUser.id,
    cleaning_job_id: '',
    amount: '',
    description: '',
    receipt_note: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showStaffNextSteps, setShowStaffNextSteps] = useState(false);
  const [filter, setFilter] = useState('active');

  function chooseReceipt(file) {
    setReceiptFile(file || null);
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceiptPreview(file ? URL.createObjectURL(file) : '');
  }

  async function add(e) {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!form.amount || Number(form.amount) <= 0 || !form.description.trim()) {
      setError('Wpisz kwotę i krótki opis zakupu.');
      return;
    }
    try {
      const data = new FormData();
      data.append('user_id', safeUser.id || '');
      data.append('cleaning_job_id', form.cleaning_job_id || '');
      data.append('amount', String(Number(form.amount)));
      data.append('description', form.description.trim());
      data.append('receipt_note', form.receipt_note.trim());
      if (receiptFile) data.append('receipt_photo', receiptFile);
      await api('/expenses', { method: 'POST', body: data });
      window.dispatchEvent(new CustomEvent('hermes:christ-egg', { detail: { seed: 'expense:' + Date.now() } }));
      setForm({...form, amount: '', description: '', receipt_note: ''});
      chooseReceipt(null);
      setShowStaffNextSteps(isStaff);
      setMessage(isStaff ? 'Zakup zapisany. Nic więcej nie musisz robić.' : 'Zakup dodany.');
      await safeReload?.();
    } catch (err) {
      setError(err.message || 'Nie udało się dodać zakupu.');
    }
  }

  const filteredExpenses = filterExpenses(safeExpenses, filter);
  const total = safeExpenses.reduce((a, b) => a + Number(b.amount), 0);
  const payableRows = safeExpenses.filter(e => !isReimbursed(e) && (e.review_status || 'pending') === 'ok');
  const clarifyRows = safeExpenses.filter(e => !isReimbursed(e) && (e.review_status || 'pending') === 'needs_clarification');
  const checkingRows = safeExpenses.filter(e => !isReimbursed(e) && !['ok', 'needs_clarification'].includes(e.review_status || 'pending'));
  const archiveRows = safeExpenses.filter(isReimbursed);
  const todo = payableRows.reduce((a, b) => a + Number(b.amount), 0);

  return (
    <section>
      <PageHeader
        icon="🧾"
        title={isStaff ? 'Zakupy do zwrotu' : 'Zakupy'}
        description={isStaff
          ? 'Wpisz zakup, dodaj zdjęcie paragonu i zapisz. Michał zobaczy to do zwrotu.'
          : 'Materiały i usługi zakupione do apartamentu. Śledź wydatki, paragony i zwroty.'}
        helpKey="expenses"
        stats={[
          { label: 'Pozycji', value: safeExpenses.length, tone: 'info' },
          { label: 'Suma', value: money(total) },
          { label: 'Do zwrotu', value: money(todo), tone: todo > 0 ? 'danger' : 'success' },
          { label: 'Do doprecyz.', value: clarifyRows.length, tone: clarifyRows.length ? 'warning' : 'success' },
          { label: 'Archiwum', value: archiveRows.length, tone: archiveRows.length ? 'info' : 'success' },
        ]}
      />

      {message && <div className="success">{message}</div>}
      {isStaff && showStaffNextSteps && <StaffExpenseNextSteps />}
      {error && <div className="error">{error}</div>}

      <form className={`card form ${isStaff ? 'staff-expense-form' : ''}`} onSubmit={add}>
        <h2>{isStaff ? 'Dodaj zakup dla Marii' : 'Dodaj zakup'}</h2>
        <select
          value={form.cleaning_job_id}
          onChange={e => setForm({...form, cleaning_job_id: e.target.value})}
        >
          <option value="">Bez przypisania do sprzątania</option>
          {visibleJobs.map(j => (
            <option key={j.id} value={j.id}>{jobLabel(j, isStaff)}</option>
          ))}
        </select>
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="Kwota, np. 18.50"
          value={form.amount}
          onChange={e => setForm({...form, amount: e.target.value})}
        />
        <input
          placeholder="Co kupiłaś? np. papier, mydło"
          value={form.description}
          onChange={e => setForm({...form, description: e.target.value})}
        />
        <label className="receipt-upload">
          <span>📷 Dodaj zdjęcie paragonu</span>
          <small>Zrób zdjęcie telefonem albo wybierz z galerii</small>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={e => chooseReceipt(e.target.files?.[0])}
          />
        </label>
        {receiptPreview && (
          <div className="receipt-preview">
            <img src={receiptPreview} alt="Podgląd paragonu" />
            <button type="button" className="secondary" onClick={() => chooseReceipt(null)}>Usuń zdjęcie</button>
          </div>
        )}
        <input
          placeholder="Notatka opcjonalna, np. numer paragonu"
          value={form.receipt_note}
          onChange={e => setForm({...form, receipt_note: e.target.value})}
        />
        <button aria-label={isStaff ? 'Zapisz zakup do zwrotu' : 'Dodaj zakup'}>{isStaff ? 'Zapisz zakup do zwrotu' : 'Dodaj'}</button>
      </form>

      <ExpenseFilters
        filter={filter}
        setFilter={setFilter}
        rows={safeExpenses}
        counts={{
          active: countByFilter(safeExpenses, 'active'),
          payable: payableRows.length,
          clarify: clarifyRows.length,
          checking: checkingRows.length,
          archive: archiveRows.length,
          all: safeExpenses.length,
        }}
      />

      {isStaff ? <StaffExpenseList rows={filteredExpenses} jobs={safeJobs} filter={filter} /> : (
        <Table
          rows={filteredExpenses}
          cols={[
            ['created_at', 'Data'],
            ['user_name', 'Osoba'],
            ['amount', 'Kwota'],
            ['description', 'Opis'],
            ['receipt_photo', 'Paragon'],
            ['review_status', 'Weryfikacja paragonu'],
            ['reimbursement', 'Zwrot pieniędzy']
          ]}
          renderers={{
            reimbursement: (row) => <ReimbursementCell row={row} reload={safeReload} />
          }}
          actionLabel="Decyzja"
          adminActions={(row) => <AdminExpenseActions row={row} reload={safeReload} />}
          emptyText={emptyExpenseText(filter)}
        />
      )}
    </section>
  );
}

function ExpenseFilters({ filter, setFilter, rows, counts }) {
  const filters = [
    { key: 'active', label: 'Aktywne', hint: 'wszystko poza zwróconymi' },
    { key: 'payable', label: 'Do zwrotu', hint: 'zatwierdzone i niezapłacone' },
    { key: 'clarify', label: 'Do doprecyzowania', hint: 'czeka na wyjaśnienie' },
    { key: 'checking', label: 'W sprawdzeniu', hint: 'AI lub admin sprawdza' },
    { key: 'archive', label: 'Archiwum', hint: 'zwrócone / zamknięte' },
    { key: 'all', label: 'Wszystkie', hint: 'pełna historia' },
  ];
  if (!rows.length) return null;
  return (
    <div className="expense-filter-card card">
      <div>
        <strong>Widok zakupów</strong>
        <small>„Odrzucone” traktujemy jako „Do doprecyzowania”; zamknięte pozycje trafiają do archiwum po zwrocie.</small>
      </div>
      <div className="filter-tabs expense-filter-tabs" role="tablist" aria-label="Filtr zakupów">
        {filters.map(item => (
          <button
            key={item.key}
            type="button"
            className={filter === item.key ? 'active' : ''}
            onClick={() => setFilter(item.key)}
            title={item.hint}
          >
            {item.label} <span className="badge">{counts[item.key] || 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StaffExpenseNextSteps() {
  return (
    <div className="card staff-next-steps" role="status" aria-live="polite">
      <strong>Co dalej?</strong>
      <ol>
        <li>AI sprawdzi zdjęcie paragonu w tle.</li>
        <li>Jeśli coś będzie niejasne, zobaczysz prostą prośbę o doprecyzowanie tutaj.</li>
        <li>Michał potwierdzi zwrot pieniędzy — status zmieni się na „Zwrócone”.</li>
      </ol>
    </div>
  );
}

function ReceiptThumb({ path, label = 'Paragon' }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!path) return;
    let alive = true;
    const token = localStorage.getItem('token');
    fetch(receiptUrl(path), { headers: token ? {Authorization: `Bearer ${token}`} : {} })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => {
        if (!alive || !blob) return;
        const objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!path) return <span>Brak</span>;
  if (!url) return <span>Ładowanie zdjęcia…</span>;
  return <a href={url} target="_blank" rel="noreferrer"><img className="receipt-thumb" src={url} alt={label} /></a>;
}

function emptyExpenseText(filter) {
  if (filter === 'archive') return 'Archiwum jest puste — nie ma jeszcze zwróconych zakupów.';
  if (filter === 'payable') return 'Nie ma zakupów gotowych do zwrotu.';
  if (filter === 'clarify') return 'Nie ma zakupów do doprecyzowania.';
  if (filter === 'checking') return 'Nie ma zakupów w sprawdzeniu.';
  return 'Nie ma jeszcze zapisanych zakupów w tym widoku.';
}

function StaffExpenseList({ rows, jobs, filter }) {
  if (!rows.length) {
    return <div className="card empty">{emptyExpenseText(filter)}</div>;
  }
  return (
    <div className="expense-cards">
      {rows.map(row => {
        const job = jobs.find(j => j.id === row.cleaning_job_id);
        return (
          <article className="card expense-card" key={row.id}>
            <div>
              <strong>{row.description}</strong>
              <small>{row.created_at}{job ? ` · ${jobLabel(job, true)}` : ''}</small>
              {row.receipt_note && <small>Notatka: {row.receipt_note}</small>}
              {row.receipt_photo && <ReceiptThumb path={row.receipt_photo} />}
              <ReviewStatus row={row} />
            </div>
            <div className="expense-side">
              <strong>{money(row.amount)}</strong>
              <span className={reimbursementState(row).className}>
                {reimbursementState(row).label}
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function Table({ rows, cols, adminActions, renderers = {}, actionLabel = 'Akcje', emptyText = 'Brak zakupów w tym widoku.' }) {
  if (!rows.length) return <div className="card empty">{emptyText}</div>;
  return (
    <div className="card table">
      <table>
        <thead>
          <tr>{cols.map(c => <th key={c[0]}>{c[1]}</th>)}{adminActions ? <th>{actionLabel}</th> : null}</tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx}>
              {cols.map(c => <td key={c[0]}>{renderers[c[0]] ? renderers[c[0]](r) : renderCell(r, c[0])}</td>)}
              <td>{adminActions(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminExpenseActions({ row, reload }) {
  const status = row.review_status || 'pending';
  async function decide(decision) {
    const note = decision === 'approve'
      ? prompt('Komentarz do zatwierdzenia (opcjonalny):', 'OK, do zwrotu.')
      : prompt('Co Maria ma doprecyzować?', 'Doprecyzuj proszę ten zakup.');
    if (note === null) return;
    await api(`/expenses/${row.id}/admin-review`, { method: 'POST', body: JSON.stringify({ decision, message: note || '' }), headers: { 'Content-Type': 'application/json' } });
    await reload?.();
  }
  async function editExpense() {
    const amount = prompt('Nowa kwota:', String(row.amount || ''));
    if (amount === null) return;
    const description = prompt('Nowy opis:', row.description || '');
    if (description === null) return;
    const receipt_note = prompt('Notatka do paragonu:', row.receipt_note || '');
    if (receipt_note === null) return;
    await api(`/expenses/${row.id}`, { method: 'PATCH', body: JSON.stringify({ amount: Number(amount), description, receipt_note }), headers: { 'Content-Type': 'application/json' } });
    await reload?.();
  }
  async function cancelExpense() {
    const reason = prompt('Powód anulowania:', 'Anulowane przez admina');
    if (reason === null) return;
    await api(`/expenses/${row.id}/cancel`, { method: 'PATCH', body: JSON.stringify({ reason }), headers: { 'Content-Type': 'application/json' } });
    await reload?.();
  }
  async function deleteExpense() {
    if (!window.confirm('Usunąć zakup trwale? Tej operacji nie cofniemy.')) return;
    await api(`/expenses/${row.id}`, { method: 'DELETE' });
    await reload?.();
  }
  return (
    <div className="expense-admin-actions">
      {isReimbursed(row) ? <span className="ack-badge">Archiwum</span> : (status !== 'ok' ? (
        <button className="primary" onClick={() => decide('approve')}>OK — do zwrotu</button>
      ) : (
        <span className="ack-badge">✓ Paragon OK</span>
      ))}
      {!isReimbursed(row) && status !== 'needs_clarification' && (
        <button className="secondary" onClick={() => decide('ask')}>Doprecyzuj</button>
      )}
      <button className="secondary" onClick={editExpense}>Edytuj</button>
      {!isReimbursed(row) && <button className="secondary" onClick={cancelExpense}>Anuluj</button>}
      <button className="danger" onClick={deleteExpense}>Usuń</button>
    </div>
  );
}

function ReimbursementCell({ row, reload }) {
  const confirmed = !!row.reimbursement_confirmed_at;
  async function confirmReimbursement() {
    await api(`/expenses/${row.id}/confirm-reimbursement`, { method: 'POST', body: JSON.stringify({}), headers: { 'Content-Type': 'application/json' } });
    await reload?.();
  }
  async function unconfirmReimbursement() {
    if (!window.confirm('Cofnąć potwierdzenie zwrotu?')) return;
    await api(`/expenses/${row.id}/unconfirm-reimbursement`, { method: 'POST' });
    await reload?.();
  }
  if (confirmed) {
    return (
      <div className="reimb-cell">
        <span className="ack-badge">💸 Zwrócone</span>
        <small>
          {formatDate(row.reimbursement_confirmed_at)} {formatClock(row.reimbursement_confirmed_at)}
          {row.reimbursement_confirmed_by && <> · {row.reimbursement_confirmed_by}</>}
        </small>
        <button className="secondary small" onClick={unconfirmReimbursement}>Cofnij</button>
      </div>
    );
  }
  if ((row.review_status || 'pending') !== 'ok') {
    const state = reimbursementState(row);
    return (
      <div className="reimb-cell">
        <span className={state.className}>{state.label}</span>
        <small>{row.review_status === 'needs_clarification' ? 'Najpierw doprecyzowanie.' : 'Najpierw sprawdzenie paragonu.'}</small>
      </div>
    );
  }
  return (
    <div className="reimb-cell">
      <span className="expense-pending">Do zwrotu</span>
      <button className="primary small" onClick={confirmReimbursement}>Potwierdź zwrot</button>
    </div>
  );
}

function renderCell(row, key) {
  if (key === 'reimbursed' || key === 'reimbursement_confirmed') return reimbursementState(row).label;
  if (key === 'receipt_photo') return <ReceiptThumb path={row[key]} />;
  if (key === 'review_status') return <ReviewStatus row={row} />;
  if (key === 'amount') return money(row[key]);
  return row[key];
}
